import { useEffect, useId } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Play } from "lucide-react";

import { useQueueJob, type Workflow } from "@/features/workflows/api";
import {
  availability,
  describeQueueError,
  queueJobSchema,
  requiredMode,
  workflowOptionLabel,
  type QueueJobValues,
} from "@/features/dashboard/queueJob";

interface Props {
  workflows: Workflow[];
  /** Entity code → display name, when the entities API has answered. */
  entityNames: ReadonlyMap<string, string>;
  onClose: () => void;
  onStarted?: (jobId: string) => void;
}

const SELECT_CLS =
  "w-full rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-2 text-sm text-ink-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-60";
const LABEL_CLS = "text-[11px] uppercase tracking-wider text-ink-4";

// Zod is the single schema; this adapts it to React Hook Form without
// pulling in another dependency.
const resolver: Resolver<QueueJobValues> = async (values) => {
  const parsed = queueJobSchema.safeParse(values);
  if (parsed.success) return { values: parsed.data, errors: {} };
  const errors: Record<string, { type: string; message: string }> = {};
  for (const issue of parsed.error.issues) {
    const field = String(issue.path[0] ?? "root");
    errors[field] ??= { type: issue.code, message: issue.message };
  }
  return { values: {}, errors };
};

export function QueueJobForm({ workflows, entityNames, onClose, onStarted }: Props) {
  const ids = useId();
  const qc = useQueryClient();
  const queue = useQueueJob();
  const { register, handleSubmit, watch, setValue } = useForm<QueueJobValues>({
    // Nothing is preselected: a job starts only after the user picks
    // both the entity and the workflow.
    defaultValues: { entityCode: "", workflowId: "", mode: "smoke" },
    resolver,
  });

  const entityCode = watch("entityCode");
  const workflowId = watch("workflowId");

  const entityCodes = Array.from(new Set(workflows.map((w) => w.entityCode))).sort();
  const inEntity = workflows.filter((w) => w.entityCode === entityCode);
  const unavailable = inEntity.filter((w) => !availability(w).ok);
  const selected = inEntity.find((w) => w.id === workflowId);
  const canStart = selected !== undefined && availability(selected).ok && !queue.isPending;
  // Some workflows only make sense as a full run (the API refuses a
  // smoke request for them). Choose it for the user and say why.
  const forcedMode = requiredMode(selected);
  useEffect(() => {
    if (forcedMode) setValue("mode", forcedMode);
  }, [forcedMode, setValue]);

  const submit = handleSubmit((values) => {
    const workflow = workflows.find(
      (w) => w.id === values.workflowId && w.entityCode === values.entityCode,
    );
    if (!workflow || !availability(workflow).ok) return;
    queue.mutate(
      {
        workflow_id: workflow.id,
        entity_code: workflow.entityCode,
        inputs: {},
        priority_tier: "urgent",
        test_mode: true,
        mode: requiredMode(workflow) ?? values.mode,
      },
      {
        onSuccess: (job) => {
          qc.invalidateQueries({ queryKey: ["approvals"] });
          onStarted?.(job.id);
          onClose();
        },
      },
    );
  });

  const error = queue.error ? describeQueueError(queue.error, selected) : null;

  return (
    <form onSubmit={submit} noValidate>
      <div className="space-y-4 p-4">
        <p className="rounded-md border border-status-approval/40 bg-status-approval-bg p-3 text-xs text-ink-2">
          <strong className="text-ink-1">Starts a real run.</strong> The job is sent to a live
          runner as soon as you select Start. The test flag only labels the job. It does not stop
          the run from reaching the portal.
        </p>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-sm border border-status-failed/40 bg-status-failed-bg p-2 text-xs text-status-failed"
          >
            {error.kind === "denied" && <Lock size={14} className="mt-0.5 shrink-0" aria-hidden />}
            <div>
              <div>{error.message}</div>
              {error.correlationId && (
                <div className="mt-1 font-mono text-[10px]">Reference: {error.correlationId}</div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label htmlFor={`${ids}-entity`} className={LABEL_CLS}>
            Entity
          </label>
          <select
            id={`${ids}-entity`}
            className={SELECT_CLS}
            {...register("entityCode", { onChange: () => setValue("workflowId", "") })}
          >
            <option value="">Select an entity</option>
            {entityCodes.map((code) => (
              <option key={code} value={code}>
                {entityNames.has(code)
                  ? `${code.toUpperCase()} · ${entityNames.get(code)}`
                  : code.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={`${ids}-workflow`} className={LABEL_CLS}>
            Workflow
          </label>
          <select
            id={`${ids}-workflow`}
            className={SELECT_CLS}
            disabled={!entityCode}
            aria-describedby={unavailable.length > 0 ? `${ids}-unavailable` : undefined}
            {...register("workflowId")}
          >
            <option value="">{entityCode ? "Select a workflow" : "Select an entity first"}</option>
            {inEntity.map((w) => (
              <option key={w.id} value={w.id} disabled={!availability(w).ok}>
                {workflowOptionLabel(w)}
              </option>
            ))}
          </select>
          {unavailable.length > 0 && (
            <ul id={`${ids}-unavailable`} className="space-y-0.5 text-[11px] text-ink-3">
              {unavailable.map((w) => {
                const a = availability(w);
                return <li key={w.id}>{`${w.name}: ${a.ok ? "" : a.note}.`}</li>;
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={`${ids}-mode`} className={LABEL_CLS}>
            Run type
          </label>
          <select
            id={`${ids}-mode`}
            className={SELECT_CLS}
            aria-describedby={forcedMode ? `${ids}-mode-note` : undefined}
            {...register("mode")}
          >
            <option value="smoke" disabled={forcedMode === "full"}>
              Smoke: open the first allowed page and take a screenshot per phase
            </option>
            <option value="full">Full: run every phase with the agent</option>
          </select>
          {forcedMode && (
            <p id={`${ids}-mode-note`} className="text-[11px] text-ink-3">
              This workflow only runs as a Full run. A smoke run would skip its phases.
            </p>
          )}
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-paper-line p-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-sm text-ink-1 hover:bg-paper-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canStart}
          className="flex items-center gap-1 rounded-sm border border-teal-600 bg-teal-500 px-4 py-1.5 text-sm font-medium text-paper-3 hover:bg-teal-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-60"
        >
          <Play size={12} aria-hidden /> {queue.isPending ? "Starting…" : "Start"}
        </button>
      </footer>
    </form>
  );
}
